import React, { createContext, useContext, useState, useCallback } from 'react';

const TutorialContext = createContext();

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

// Local storage key for tracking tutorial completion
const TUTORIAL_COMPLETED_KEY = 'vetted_tutorial_completed';
const TUTORIAL_SKIPPED_KEY = 'vetted_tutorial_skipped';

// Tutorial step definitions - Interactive flow that requires user actions
export const TUTORIAL_STEPS = [
  // === CREATE LIST FLOW ===
  {
    id: 'click-add-button',
    title: 'Create Your First List',
    content: 'Tap the + button to open the menu.',
    target: '.btn-new-list',
    placement: 'bottom-left',
    action: 'click',
    waitFor: 'menuOpened', // Wait for the dropdown menu to open
    allowTargetClick: true,
  },
  {
    id: 'click-new-list',
    title: 'New List',
    content: 'Tap "New List" to create your first list.',
    target: '.add-menu-item',
    placement: 'bottom-left',
    action: 'click',
    waitFor: 'navigation:/lists/new', // Wait for navigation to NewList page
    allowTargetClick: true,
  },
  {
    id: 'fill-list-form',
    title: 'Name Your List',
    content: 'Complete the form, then tap Create List.',
    target: null,
    placement: 'center',
    action: 'submit',
    waitFor: 'listCreated',
    hideTooltip: true, // Hide tooltip so user can interact with the page
  },

  // === ADD FIRST PLACE ===
  {
    id: 'list-created-intro',
    title: 'Nice! Your List is Ready',
    content: 'Now let\'s add some places to it. Tap the + button to add your first place.',
    target: '[title="Add Place"]',
    placement: 'bottom',
    action: 'click',
    waitFor: 'navigation:/list/', // Wait for navigation to AddPlace page (matches /list/:id/add-place)
    waitForSuffix: '/add-place',
    allowTargetClick: true,
  },
  {
    id: 'search-first-place',
    title: 'Search for a Place',
    content: 'Search for a place and tap "Add Place" to save it.',
    target: null,
    placement: 'center',
    action: 'wait',
    waitFor: 'placeAddedAndClosed',
    hideTooltip: true, // Hide tooltip so user can interact with the page
  },

  // === LIST FEATURES ===
  {
    id: 'list-buttons-intro',
    title: 'List Actions',
    content: 'Here are your list tools: Edit Details, Add More Places, Duplicate, Import, and Premium-only features like Trip Planning and View on Map.',
    target: '.header-actions-grid',
    placement: 'bottom',
  },
  {
    id: 'place-card-intro',
    title: 'Your Saved Places',
    content: 'Each place shows your designation and other info. Tap a place to see more details, or tap the edit icon to make changes.',
    target: '.place-card',
    placement: 'top',
  },
  {
    id: 'back-to-lists',
    title: 'Back to Your Lists',
    content: 'Tap "Back" to see all your lists. Let\'s explore the rest of the app!',
    target: '.back-btn',
    placement: 'bottom-right',
    action: 'click',
    waitFor: 'navigation:/lists',
    allowTargetClick: true,
  },

  // === APP NAVIGATION ===
  {
    id: 'discover-nav',
    title: 'Discover New Lists',
    content: 'Tap Discover to find public lists and new people to follow.',
    target: '[aria-label="Discover"]',
    placement: 'top',
    action: 'click',
    waitFor: 'navigation:/discover',
    allowTargetClick: true,
  },
  {
    id: 'discover-click-list',
    title: 'Explore a List',
    content: 'Tap on any list to see what places it contains.',
    target: '.list-preview-card',
    placement: 'bottom',
    action: 'click',
    waitFor: 'navigation:/list/',
    allowTargetClick: true,
  },
  {
    id: 'follow-list-button',
    title: 'Follow This List',
    content: 'Tap the follow icon to follow this list. You\'ll get notified when new places are added!',
    target: '.external-list-actions .btn-icon-header:first-child',
    placement: 'bottom',
    disableBack: true,
  },
  {
    id: 'save-place-button',
    title: 'Save Places to Your Lists',
    content: 'See a place you like? Tap the Plus icon on any place card to save it to one of your own lists.',
    target: '.place-save-badge',
    targetFallback: '.place-card',
    placement: 'left',
  },
  {
    id: 'following-page-hint',
    title: 'Find Your Followed Lists',
    content: 'Lists you follow will appear on the Following page. Check back there anytime to see updates!',
    target: '[aria-label="Following"]',
    placement: 'top',
  },
  {
    id: 'map-nav',
    title: 'Map View (Premium)',
    content: 'See all your saved places on an interactive map. You can also add places to your lists from here!',
    target: '[aria-label="Map"]',
    placement: 'top',
  },
  {
    id: 'notifications-nav',
    title: 'Notifications',
    content: 'See friend requests, new followers, and updates when lists you follow change.',
    target: '[aria-label*="Notifications"]',
    placement: 'top',
  },

  // === PREMIUM ===
  {
    id: 'premium-comparison',
    title: 'Free vs Premium',
    content: 'Vibary is free with some limits. Premium unlocks everything:',
    target: null,
    placement: 'center',
    showPremiumComparison: true,
    showAndMore: true,
  },

  // === COMPLETE ===
  {
    id: 'tutorial-complete',
    title: 'You\'re All Set!',
    content: 'You\'ve created your first list with places! Keep adding lists, discover new places, and connect with friends. Happy exploring!',
    target: null,
    placement: 'center',
    isFinal: true,
  },
];

export const TutorialProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isWaitingForAction, setIsWaitingForAction] = useState(false);
  const [skipNextNavCheck, setSkipNextNavCheck] = useState(false);
  const [hasCompletedTutorial, setHasCompletedTutorial] = useState(() => {
    return localStorage.getItem(TUTORIAL_COMPLETED_KEY) === 'true';
  });
  const [hasSkippedTutorial, setHasSkippedTutorial] = useState(() => {
    return localStorage.getItem(TUTORIAL_SKIPPED_KEY) === 'true';
  });

  const currentStep = TUTORIAL_STEPS[currentStepIndex];

  // Start the tutorial
  const startTutorial = useCallback(() => {
    setCurrentStepIndex(0);
    setIsActive(true);
    setIsWaitingForAction(false);
  }, []);

  // End the tutorial (complete or skip)
  const endTutorial = useCallback((completed = false) => {
    setIsActive(false);
    setCurrentStepIndex(0);
    setIsWaitingForAction(false);

    if (completed) {
      localStorage.setItem(TUTORIAL_COMPLETED_KEY, 'true');
      setHasCompletedTutorial(true);
    } else {
      localStorage.setItem(TUTORIAL_SKIPPED_KEY, 'true');
      setHasSkippedTutorial(true);
    }

    // Dispatch event to trigger install prompt
    window.dispatchEvent(new CustomEvent('tutorialEnded', { detail: { completed } }));
  }, []);

  // Go to next step
  const nextStep = useCallback(() => {
    if (currentStepIndex < TUTORIAL_STEPS.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
      setIsWaitingForAction(false);
      setSkipNextNavCheck(false);
    } else {
      endTutorial(true);
    }
  }, [currentStepIndex, endTutorial]);

  // Go to previous step
  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      setIsWaitingForAction(false);
      // Skip navigation check on next render to prevent immediate re-advance
      setSkipNextNavCheck(true);
    }
  }, [currentStepIndex]);

  // Skip to a specific step by ID
  const goToStep = useCallback((stepId) => {
    const index = TUTORIAL_STEPS.findIndex(s => s.id === stepId);
    if (index !== -1) {
      setCurrentStepIndex(index);
      setIsWaitingForAction(false);
    }
  }, []);

  // Notify that a user action occurred (for steps that wait for actions)
  // Actions: 'listCreated', 'placeAdded', 'placeAddedAndClosed', 'navigation', 'menuOpened'
  const notifyAction = useCallback((actionType, data = {}) => {
    if (!isActive || !currentStep) return;

    const waitFor = currentStep.waitFor;
    if (!waitFor) return;

    // Parse the waitFor pattern
    // Formats: 'navigation:/path', 'listCreated', 'placeAdded', 'placeAddedAndClosed', 'menuOpened'

    if (waitFor.startsWith('navigation:')) {
      // Skip navigation check if we just went back
      if (skipNextNavCheck) {
        setSkipNextNavCheck(false);
        return;
      }
      const expectedPath = waitFor.replace('navigation:', '');
      if (actionType === 'navigation' && data.path?.startsWith(expectedPath)) {
        // If step has a waitForSuffix, also check that the path ends with it
        if (currentStep.waitForSuffix) {
          if (data.path?.endsWith(currentStep.waitForSuffix)) {
            nextStep();
          }
        } else {
          nextStep();
        }
      }
    } else if (waitFor === 'listCreated' && actionType === 'listCreated') {
      nextStep();
    } else if (waitFor === 'placeAdded' && actionType === 'placeAdded') {
      nextStep();
    } else if (waitFor === 'placeAddedAndClosed' && actionType === 'placeAddedAndClosed') {
      nextStep();
    } else if (waitFor === 'menuOpened' && actionType === 'menuOpened') {
      nextStep();
    }
  }, [isActive, currentStep, nextStep, skipNextNavCheck]);

  // Check if tutorial should be shown for first-time users
  const shouldShowTutorial = useCallback(() => {
    return !hasCompletedTutorial && !hasSkippedTutorial;
  }, [hasCompletedTutorial, hasSkippedTutorial]);

  // Reset tutorial (for testing or re-showing)
  const resetTutorial = useCallback(() => {
    localStorage.removeItem(TUTORIAL_COMPLETED_KEY);
    localStorage.removeItem(TUTORIAL_SKIPPED_KEY);
    setHasCompletedTutorial(false);
    setHasSkippedTutorial(false);
  }, []);

  const value = {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps: TUTORIAL_STEPS.length,
    isWaitingForAction,
    skipNextNavCheck,
    setSkipNextNavCheck,
    hasCompletedTutorial,
    hasSkippedTutorial,
    startTutorial,
    endTutorial,
    nextStep,
    prevStep,
    goToStep,
    notifyAction,
    shouldShowTutorial,
    resetTutorial,
    setIsWaitingForAction,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};
